import React, { useState, useEffect, useMemo } from "react";
import { Gem } from "lucide-react";
import logger from "../../utils/logger";
import { CRYPTO_PAYMENT_DISCOUNT_PERCENT } from "../../utils/paymentDiscounts";

/**
 * Countdown Timer Component for First Purchase Bonuses
 * Uses real user signup date and purchase timestamps for accurate countdown
 */
const CountdownTimer = ({ timerData, offerType = "first_time" }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  // setIsExpired is invoked elsewhere in the timer logic; the boolean
  // itself isn't read in render (the timer pad becomes empty when
  // timeLeft is null).
  const [, setIsExpired] = useState(false);

  useEffect(() => {
    if (!timerData) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      let targetDate;

      if (offerType === "first_time") {
        // First-time purchase: 7 days from signup, resets every 7 days if not purchased
        const signupDate = new Date(timerData.userSignupDate);
        const daysSinceSignup = Math.floor(
          (now - signupDate) / (1000 * 60 * 60 * 24),
        );
        const cycleNumber = Math.floor(daysSinceSignup / 7);
        targetDate = new Date(
          signupDate.getTime() + (cycleNumber + 1) * 7 * 24 * 60 * 60 * 1000,
        );
      } else if (offerType === "second_time") {
        // Second-time purchase: 7 days from first purchase, resets every 7 days if not purchased
        if (!timerData.firstTimePurchaseDate) {
          setIsExpired(true);
          return;
        }
        const firstPurchaseDate = new Date(timerData.firstTimePurchaseDate);
        const daysSinceFirstPurchase = Math.floor(
          (now - firstPurchaseDate) / (1000 * 60 * 60 * 24),
        );
        const cycleNumber = Math.floor(daysSinceFirstPurchase / 7);
        targetDate = new Date(
          firstPurchaseDate.getTime() +
            (cycleNumber + 1) * 7 * 24 * 60 * 60 * 1000,
        );
      }

      const distance = targetDate.getTime() - now.getTime();

      if (distance <= 0) {
        // Timer expired, will reset in next cycle
        setIsExpired(false); // Keep showing timer as it resets
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        ),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
      setIsExpired(false);
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [timerData, offerType]);

  if (!timeLeft) {
    return (
      <div className="bg-gradient-to-r from-red-500 to-red-700 text-white text-xs font-bold px-3 py-1 text-center border-b border-red-400/30">
        <div className="flex items-center justify-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-3 h-3"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-red-500 to-red-700 text-white text-xs font-bold px-3 py-1 text-center border-b border-red-400/30">
      <div className="flex items-center justify-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-3 h-3"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
        <span>
          {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m{" "}
          {timeLeft.seconds}s
        </span>
      </div>
    </div>
  );
};

/**
 * Weekly Countdown Timer Component for Weekly Purchase Offers
 */
const WeeklyCountdownTimer = ({ lastPurchaseTime }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!lastPurchaseTime) {
      setIsExpired(true);
      return;
    }

    const calculateTimeLeft = () => {
      const lastPurchase = new Date(lastPurchaseTime);
      const nextAvailable = new Date(
        lastPurchase.getTime() + 7 * 24 * 60 * 60 * 1000,
      ); // 7 days later
      const now = new Date();
      const diff = nextAvailable.getTime() - now.getTime();

      if (diff <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
      setIsExpired(false);
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [lastPurchaseTime]);

  if (isExpired || !timeLeft) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-xs font-bold px-3 py-1 text-center border-b border-blue-400/30">
      <div className="flex items-center justify-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-3 h-3"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
        <span>
          {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m{" "}
          {timeLeft.seconds}s
        </span>
      </div>
    </div>
  );
};

/**
 * Reusable OfferCard Component
 *
 * Displays an offer with:
 * - Image, name, price (with crossed out original price)
 * - Gold coins and sweep coins
 * - Bonus percentage (if > 0)
 * - Buy now button
 * - Expandable terms section (if limits exist)
 * - Countdown timer for first-time bonuses
 */
const OfferCard = ({
  offer,
  onBuyNow,
  className = "",
  showTimer = false,
  lastPurchaseTime = null, // For weekly cooldown tracking
  timerData = null, // Timer data for first-time purchase countdown
}) => {
  const {
    name,
    usd_price,
    original_price,
    gold_coins,
    sweep_coins,
    feathers,
    bonus_percentage,
    image_url,
    image_alt_text,
    badge_type,
    has_discount,
    has_bonus,
    purchase_limit,
    weekly_limit,
  } = offer;

  // Check if this is a weekly offer (cooldown gating handled below).
  const isWeeklyOffer = badge_type === "weekly_special" || weekly_limit;

  // Calculate if weekly cooldown has expired
  const isWeeklyCooldownExpired = useMemo(() => {
    if (!lastPurchaseTime || !isWeeklyOffer) return true;

    const lastPurchase = new Date(lastPurchaseTime);
    const nextAvailable = new Date(
      lastPurchase.getTime() + 7 * 24 * 60 * 60 * 1000,
    );
    const now = new Date();

    return now >= nextAvailable;
  }, [lastPurchaseTime, isWeeklyOffer]);

  // Debug: Log the first item specifically
  if (name === "Starter Pack" || name?.toLowerCase().includes("starter")) {
    logger.debug("Starter Pack Debug:", {
      name,
      bonus_percentage,
      typeof_bonus: typeof bonus_percentage,
      has_bonus,
      original_price,
      typeof_original: typeof original_price,
      has_discount,
    });
  }

  // (Removed unused getBadgeStyles + getBadgeLabel helpers — refactor
  // leftovers from when badges rendered inside this component. Badges
  // are now rendered by the parent via OfferBadge.)

  const handleBuyNow = () => {
    // Don't allow purchase if in weekly cooldown
    if (isWeeklyOffer && !isWeeklyCooldownExpired) {
      return;
    }

    if (onBuyNow) {
      onBuyNow(offer);
    }
  };

  // Determine if card should be disabled
  const isDisabled = isWeeklyOffer && !isWeeklyCooldownExpired;

  return (
    <div
      className={`relative rounded-xl shadow-lg transition-all duration-300 overflow-hidden offers-skeleton-bg ${
        isDisabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:shadow-xl cursor-pointer hover:scale-105 active:scale-95 focus-visible:ring-2 focus-visible:ring-primary/40"
      } ${className} group select-none`}
      onClick={handleBuyNow}
      onKeyDown={(e) => {
        if (isDisabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleBuyNow();
        }
      }}
      tabIndex={isDisabled ? -1 : 0}
      role="button"
      aria-disabled={isDisabled}
    >
      {/* Countdown Timer - Show for first-time bonuses */}
      {showTimer && (
        <div className="absolute top-0 left-0 right-0 z-20">
          <CountdownTimer
            timerData={timerData}
            offerType={
              name?.toLowerCase().includes("2nd purchase")
                ? "second_time"
                : "first_time"
            }
          />
        </div>
      )}

      {/* Weekly Countdown Timer - Show for weekly offers in cooldown */}
      {isWeeklyOffer && !isWeeklyCooldownExpired && lastPurchaseTime && (
        <div className="absolute top-0 left-0 right-0 z-20">
          <WeeklyCountdownTimer lastPurchaseTime={lastPurchaseTime} />
        </div>
      )}

      {/* Content Container */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Main Content Area - Takes most space (reserve space for timer consistently) */}
        <div className={`flex-1 flex flex-col p-3 md:p-4 pt-7 md:pt-7`}>
          {/* Package Name - Simple text on card */}
          <div className="text-white font-bold text-sm md:text-base text-center mb-2 md:mb-3">
            {name}
          </div>

          {/* Package Image - Fill all available space */}
          <div className="flex justify-center mb-3 md:mb-4 flex-1">
            <img
              src={image_url || "/assets/packages/default-package.png"}
              alt={image_alt_text || name}
              className="w-full h-full max-w-32 max-h-32 md:max-w-48 md:max-h-48 object-contain"
              decoding="async"
              onError={(e) => {
                // Prevent infinite loop by only setting fallback if not already using it
                if (
                  e.target.src !==
                  window.location.origin +
                    "/assets/packages/default-package.png"
                ) {
                  e.target.src = "/assets/packages/default-package.png";
                } else {
                  // If default image also fails, hide the image and show a placeholder
                  e.target.style.display = "none";
                  const placeholder =
                    e.target.parentElement.querySelector(".image-placeholder");
                  if (!placeholder) {
                    const div = document.createElement("div");
                    div.className =
                      "image-placeholder w-full h-full max-w-32 max-h-32 md:max-w-48 md:max-h-48 flex items-center justify-center bg-card/20 rounded-lg";
                    div.innerHTML =
                      '<div class="text-gray-400 text-center"><div class="text-3xl md:text-5xl mb-1">📦</div><div class="text-xs md:text-sm">Package</div></div>';
                    e.target.parentElement.appendChild(div);
                  }
                }
              }}
            />
          </div>

          {/* Coins Section */}
          <div className="flex-1 flex flex-col justify-center">
            {/* Gold Coins - Main Large Yellow Text */}
            <div className="flex items-center justify-center gap-2 md:gap-3 mb-2 md:mb-3">
              <img
                src="/assets/coins/Gold-Coin.png"
                alt="Gold"
                className="w-6 h-6 md:w-10 md:h-10 flex-shrink-0"
                style={{
                  filter: "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))",
                }}
              />
              <span
                className="text-xl md:text-3xl font-black text-primary leading-tight"
                style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)" }}
              >
                {gold_coins.toLocaleString()}
              </span>
            </div>

            {/* CB Coins - Green Text */}
            <div className="flex items-center justify-center gap-2 md:gap-3 mb-2 md:mb-3">
              <img
                src="/assets/coins/Cluck-Coin.png"
                alt="CB"
                className="w-5 h-5 md:w-8 md:h-8 flex-shrink-0"
                style={{
                  filter: "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))",
                }}
              />
              <span
                className="text-xs md:text-lg font-bold text-green-400"
                style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)" }}
              >
                +{sweep_coins.toLocaleString()} FOR FREE
              </span>
            </div>

            {/* Feathers - White Text (same style as CB) */}
            {Number(feathers) > 0 && (
              <div className="flex items-center justify-center gap-2 md:gap-3">
                <img
                  src="/assets/3d/feather-transparent.png"
                  alt="Feathers"
                  className="w-4 h-4 md:w-6 md:h-6 flex-shrink-0"
                  style={{
                    filter: "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))",
                  }}
                />
                <span
                  className="text-xs md:text-sm font-bold text-white"
                  style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)" }}
                >
                  +{Number(feathers).toLocaleString()} FOR FREE
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Centered Price with Bonus */}
        <div
          className={`bg-black/90 border-t border-white/10 flex-shrink-0 px-3 py-2 md:px-4 md:py-3`}
        >
          <div className="text-center">
            {/* Price Row - Current and Original Price in same row */}
            <div className="flex items-center justify-center gap-2">
              <div className="text-white font-bold text-lg md:text-xl">
                ${usd_price}
              </div>
              {/* Only show original price if it exists and is greater than current price */}
              {original_price != null &&
                Number(original_price) > 0 &&
                Number(original_price) > Number(usd_price) && (
                  <div className="text-gray-400 line-through text-sm md:text-base">
                    ${original_price}
                  </div>
                )}
            </div>
            {/* Bonus Percentage + crypto-discount tag. The crypto tag is
                shown on every card unconditionally so the discount is
                merchandised consistently — checkout shows the dollar
                savings once a method is picked. */}
            <div className="mt-1 min-h-[1.25rem] md:min-h-[1.5rem] flex items-center justify-center gap-1.5 flex-wrap">
              {bonus_percentage != null && Number(bonus_percentage) > 0 && (
                <span className="text-green-400 text-xs md:text-sm font-semibold bg-green-400/10 px-2 py-1 rounded">
                  +{bonus_percentage}% Bonus
                </span>
              )}
              <span
                className="inline-flex items-center gap-1 text-primary text-[10px] md:text-xs font-semibold bg-primary/15 border border-primary/30 px-2 py-0.5 rounded"
                title={`Pay with Bitcoin, ETH, USDC or USDT to save ${CRYPTO_PAYMENT_DISCOUNT_PERCENT}% on this purchase`}
              >
                <Gem className="w-3 h-3" aria-hidden="true" />-
                {CRYPTO_PAYMENT_DISCOUNT_PERCENT}% with crypto
              </span>
            </div>

            {/* Purchase Limits - Reserve space for one line to keep alignment */}
            <div className="mt-2 text-xs text-gray-400 min-h-[1rem]">
              {purchase_limit && <div>Max: {purchase_limit}x per player</div>}
              {weekly_limit && <div>Max: {weekly_limit}x per week</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferCard;
